import type { FC } from 'react';
import { useEffect } from 'react';
import { z } from 'zod';
import type { TsoMinimal } from 'src/apis/monolith/types';
import { SearchableMultiSelect } from 'src/components/form/select/searchable-multi-select';
import { formOptionSchema } from 'src/components/form/zod-schemas';
import { useAxios } from 'src/hooks/use-axios';
import { useClientSearch } from 'src/hooks/use-client-search';

type Props = {
  label: string;
  placeholder: string;
  name?: string;
  valueField?: keyof TsoMinimal;
};

export const tsoOptionSchema = z.object({
  shortName: z.string(),
  ...formOptionSchema.shape,
});

type TsoMetaOption = z.input<typeof tsoOptionSchema>;

export const TsoSearchableMultiSelect: FC<Props> = ({
  label,
  placeholder,
  name = 'tsoId',
  valueField = 'organizationId',
}) => {
  const searchRequest = useAxios(
    (axios, baseConfig) =>
      axios.request<TsoMinimal[]>({
        ...baseConfig,
        url: `${PRISMA_CONFIG.monolithApiUrl}/tso/?detailLevel=MINIMAL`,
        method: 'GET',
      }),
    { neededOnPageLoad: false }
  );

  const tsos = searchRequest.response?.data.map(
    (tso) =>
      ({
        value: String(tso[valueField]),
        label: tso.name,
        shortName: tso.shortName,
      }) satisfies TsoMetaOption
  );

  const search = useClientSearch(
    tsos,
    (tso, search) =>
      tso.label.toLowerCase().match(search) ||
      tso.shortName.toLowerCase().match(search)
  );

  // on the very first search input we try to fetch the initial list
  useEffect(() => {
    if (!search.searchValue || searchRequest.response) return;
    searchRequest.execute();
    // oxlint-disable-next-line exhaustive-deps
  }, [search.searchValue, searchRequest.response]);

  return (
    <SearchableMultiSelect
      label={label}
      name={name}
      stacked
      placeholder={placeholder}
      {...search}
      renderOptionHint={(option) => option.shortName}
      renderSelectedOptionTooltip={(option) => (
        <>
          <strong>Short Name:</strong> {option.shortName}
        </>
      )}
    />
  );
};
